import { useCallback, useEffect, useState } from 'react';

const STORAGE_KEY = 'insights.notifications.doNotDisturb';

/**
 * Do Not Disturb: stops the badge and the toast-level noise without stopping the feed.
 *
 * Notifications keep being written and keep appearing in the panel and on the full page —
 * this only silences the *interruption*. Muting delivery would mean someone returning from
 * DND has no idea what they missed, which is the opposite of what the setting is for.
 *
 * Stored in localStorage rather than on the user record, deliberately: "don't interrupt me
 * right now" is a property of the machine you are sitting at, not of your account. The cost
 * is that it does not follow you to another browser — see the note in the panel.
 *
 * @return {Object} `isEnabled` — the current setting — and `toggle`, which flips it and
 *   persists the new value.
 */
export function useDoNotDisturb() {
  const [isEnabled, setIsEnabled] = useState(false);

  // Read after mount rather than in useState's initialiser so this stays safe if the
  // component is ever rendered without a DOM.
  useEffect(() => {
    try {
      setIsEnabled(window.localStorage.getItem(STORAGE_KEY) === 'true');
    } catch (_e) {
      // Private browsing or a storage-disabled profile. Defaulting to "not muted" is the
      // right failure: a user who cannot persist the setting should still hear about a
      // Sev-1 rather than being silently muted forever.
      setIsEnabled(false);
    }
  }, []);

  const toggle = useCallback((next: boolean) => {
    setIsEnabled(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, String(next));
    } catch (_e) {
      // Same reasoning as above — the toggle still works for this session.
    }
  }, []);

  return { isEnabled, toggle };
}
